import { Link } from 'react-router-dom';
import type { Drop } from '../data/mock';
import { useCurrency } from '../lib/currency';
import { VerifiedBadge } from './VerifiedBadge';

/**
 * The Drop Card — the heart of the brand system. Full-bleed content as hero,
 * editorial metadata beneath, mono edition count, cyan CTA. Sold-out state
 * dims the content and swaps the CTA for an outline button. See BRAND.md ▸
 * Drop Card Anatomy.
 */
export function DropCard({ drop }: { drop: Drop }) {
  const { format } = useCurrency();
  const soldOut = drop.edition.sold >= drop.edition.total;
  const href = `/drop/${drop.id}`;

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-provny-border bg-provny-surface dark:border-provny-border-dark dark:bg-provny-surface-dark">
      <Link to={href} className="relative block aspect-[4/5] overflow-hidden">
        <img
          src={drop.image}
          alt={drop.title}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
            soldOut ? 'opacity-60' : ''
          }`}
        />
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-provny-muted">
            {drop.channel}
          </span>
          <span className="text-sm text-provny-muted">@{drop.creator}</span>
          <Link to={href}>
            <h3 className="font-heading text-lg font-bold leading-snug tracking-tight transition-colors group-hover:text-provny-cyber-dim dark:group-hover:text-provny-cyber">
              {drop.title}
            </h3>
          </Link>
        </div>

        <div className="flex items-baseline justify-between font-mono text-sm">
          {soldOut ? (
            <span className="font-medium text-provny-gold">
              {drop.edition.total} / {drop.edition.total} · SOLD OUT
            </span>
          ) : (
            <>
              <span className="text-provny-muted">
                {drop.edition.sold} / {drop.edition.total}
              </span>
              <span className="font-medium text-provny-black dark:text-provny-white">{format(drop.priceSol)}</span>
            </>
          )}
        </div>

        {soldOut ? (
          <Link
            to={href}
            className="rounded-xl border border-provny-cyber-dim py-2.5 text-center text-sm font-semibold text-provny-cyber-dim transition-colors hover:bg-provny-cyber/10 dark:border-provny-cyber dark:text-provny-cyber"
          >
            View Drop
          </Link>
        ) : (
          <Link
            to={href}
            className="rounded-xl bg-provny-cyber py-2.5 text-center text-sm font-semibold text-provny-ink transition hover:bg-provny-cyber/90"
          >
            Collect Now
          </Link>
        )}

        <div className="mt-auto pt-1">
          <VerifiedBadge geneId={drop.geneId} />
        </div>
      </div>
    </article>
  );
}
